import {handleActions} from 'redux-actions';
import {Record, OrderedMap} from 'immutable';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import PlatformHelper from 'web-app/helpers/platform-helper';

import * as Actions from './actions';

const State = Record({
    isVisible: false,
    isVisibleV2: false,
    institutionId: undefined,
    billPayerId: undefined,
    inferredChildId: undefined,
    noItemsToInvoice: false,
    currentPreview: null,
    showDetails: false,
    previews: OrderedMap(),
    saving: false,
    loading: false,
});

const Preview = Record({
    id: null,
    invoice: null,
    details: null,
    warnings: null,
});

const reducer = handleActions(
    {
        [Actions.show.type]: (state, {payload}) =>
            new State({
                isVisible: true,
                billPayerId: payload.billPayerId,
                inferredChildId: payload.inferredChildId,
                institutionId: payload.institutionId,
            }),
        [Actions.hide.type]: () => new State(),
        [Actions.storeInvoicePreviewId.type]: (state, {payload}) => {
            return state.merge({
                previews: state.previews.set(payload.requestId, null),
            });
        },
        [Actions.showDetails.type]: (state, {payload}) =>
            state.merge({
                showDetails: payload.show,
            }),
        [Actions.previewLoaded.type]: (state, {payload}) => {
            const previewIds = state.previews.keySeq();
            if (previewIds.indexOf(payload.requestId) > previewIds.indexOf(state.currentPreview)) {
                return state.merge({
                    currentPreview: payload.requestId,
                });
            }
            return state;
        },
        [Actions.previewInvoiceWithGraphQL.type]: state =>
            state.merge({
                loading: true,
            }),
        [Actions.previewInvoiceWithGraphQLFailed.type]: state =>
            state.merge({
                loading: false,
            }),
        [Actions.previewInvoiceWithGraphQLSuccess.type]: (state, {payload}) => {
            let currentPreview;

            // If we're already showing details we don't need to wait for the pdf iframe to load so we switch previews
            // immediately. Same if we have previously not been able to show an invoice
            if (state.showDetails || state.noItemsToInvoice || !PlatformHelper.hasPdfInIframeSupport()) {
                currentPreview = payload.requestId;
            }
            // Otherwise use the currentPreview if present or the new requestId if this is the first time we load a PDF
            else {
                currentPreview = state.currentPreview || payload.requestId;
            }

            const invoice = payload.result.invoices.get(0);
            const hasSomeErrors = payload.result.invoices.some(entry => hasValue(entry?.errorCode));

            if (hasSomeErrors || !invoice) {
                return state.merge({
                    noItemsToInvoice: true,
                    loading: false,
                });
            }

            return state.merge({
                loading: false,
                noItemsToInvoice: false,
                currentPreview,
                previews: state.previews.set(
                    payload.requestId,
                    new Preview({
                        id: payload.requestId,
                        invoice,
                    }),
                ),
            });
        },
        [AccountItemActions.createInvoice.type]: state =>
            state.merge({
                saving: true,
            }),
        [AccountItemActions.createInvoiceSuccess.type]: () => new State(),
        [AccountItemActions.createInvoiceWithGraphQLSuccess.type]: () => new State(),
        [AccountItemActions.createInvoiceAborted.type]: state =>
            state.merge({
                saving: false,
            }),
        [AccountItemActions.createInvoiceFailed.type]: state =>
            state.merge({
                saving: false,
            }),
    },
    new State(),
);

export default reducer;
